import { Box, Grid, Stack, Typography } from "@mui/material";
import PaymentBadge from '../../common/PaymentBadge';
import React from 'react';
import { useParams } from "react-router-dom";
import { formatCurrency, formatDate } from "../../common/util";
import { useGetChannelOrderQuery } from "../../redux/commonApiCalls";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import PageTitle from "../../components/PageTitle";
import { maskAddress, maskEmail, maskPhoneNumber, shouldMaskData } from "../../helpers/UtilityHelper";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { getToken } from "../../helpers/ReduxHelpers";
import StatusChip from "../../common/StatusChip";
import CopyIcon from "../../common/CopyIcon";
import { T, SectionCard, InfoRow } from "../orders/detail/detailKit";
import AddressQualityCard from '../orders/AddressQualityCard';
import RtoRiskCard from '../orders/RtoRiskCard';
import { netraStageNote } from '../orders/netraStageNote';

// ─── UI helpers ───────────────────────────────────────────────────────────────

// Summary KPI tile — merchant-panel look (big value, uppercase label, colored top rule).
const Tile = ({ label, value, accent }) => (
    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: T.WHITE, border: `1px solid ${T.BORDER}`, borderTop: `3px solid ${accent}`, height: '100%' }}>
        <Typography sx={{ fontFamily: T.FONT, fontSize: 20, fontWeight: 800, color: T.NAVY, lineHeight: 1.2, letterSpacing: '-0.01em' }}>{value}</Typography>
        <Typography sx={{ fontFamily: T.FONT, fontSize: 11, fontWeight: 600, color: T.SUBTLE, textTransform: 'uppercase', letterSpacing: '0.4px', mt: 0.5 }}>{label}</Typography>
    </Box>
);

const FulfilledBadge = ({ value }) => (
    <Box component="span" sx={{
        display: 'inline-block', px: '8px', py: '3px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', fontFamily: T.FONT,
        bgcolor: value ? '#ECFDF5' : '#FEF2F2',
        color: value ? '#047857' : '#B91C1C',
        border: `1px solid ${value ? '#A7F3D0' : '#FECACA'}`,
    }}>
        {value ? 'Yes' : 'No'}
    </Box>
);

const gridSx = {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: T.LIGHT_BG, borderBottom: `1px solid ${T.BORDER}` },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: '#475569', fontFamily: T.FONT },
    '& .MuiDataGrid-cell': { fontFamily: T.FONT, fontSize: 12.5 },
};

// ─── Page ─────────────────────────────────────────────────────────────────────

function ViewDetails() {
    const { id } = useParams();
    const { data } = useGetChannelOrderQuery({
        andfilter: [{ "field": "channelOrderId", "operator": "eq", "value": id }],
        "offset": 0,
        "limit": 1,
        "includes": "orderName,channelOrderStatus,total_shipping_price,subtotal_price_after_discount,invoice_value,createdDate,paymentMode,merchantName,channelOrderId,order_number,shipping_address,orderItems,id,netraAddress,netraRto"
    });

    const payment_mode = data?.data[0]?.payment_mode || "";
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());
    const courier = data?.data[0]?.orderItems;
    const order = data?.data[0];

    const masked = shouldMaskData(report, order, piiAccessAllow);
    const customerName = [order?.shipping_address?.first_name, order?.shipping_address?.last_name].filter(Boolean).join(' ');
    const fullAddress = [
        order?.shipping_address?.address1,
        order?.shipping_address?.address2,
        order?.shipping_address?.city,
        order?.shipping_address?.state,
        order?.shipping_address?.country,
        order?.shipping_address?.zip,
    ].filter(Boolean).join(', ');

    const columns = [
        { field: 'units', headerName: 'Units', headerAlign: 'center', align: 'center', sortable: false, width: 80 },
        { field: 'title', headerName: 'Item', headerAlign: 'left', align: 'left', sortable: false, minWidth: 150, flex: 1 },
        {
            field: 'fullfilled_against_awb',
            headerName: 'AWB Number', headerAlign: 'left', align: 'left',
            sortable: false, minWidth: 160, flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {params.value}
                    {params.value && <CopyIcon text={params.value} />}
                </Box>
            ),
        },
        {
            field: 'fullfilled',
            headerName: 'Fulfilled', headerAlign: 'center', align: 'center',
            sortable: false, width: 100,
            renderCell: ({ row }) => <FulfilledBadge value={row?.fullfilled === true} />,
        },
        {
            field: 'dimension',
            headerName: 'Dimensions', headerAlign: 'center', align: 'center',
            sortable: false, width: 160,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                    {row?.item_length} × {row?.item_breadth} × {row?.item_height} cm
                </Box>
            ),
        },
        {
            field: 'weight',
            headerName: 'Weight', headerAlign: 'center', align: 'center',
            sortable: false, width: 110,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '12.5px', color: 'text.primary' }}>
                    {row?.weight ? `${(row.weight / 1000)} kg` : '—'}
                </Box>
            ),
        },
        {
            field: 'VOL.weight',
            headerName: 'Vol. Weight', headerAlign: 'center', align: 'center',
            sortable: false, width: 120,
            renderCell: ({ row }) => {
                const vol = row?.item_length * row?.item_breadth * row?.item_height;
                return (
                    <Box sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        {vol ? `${(vol / 5000)} kg` : '—'}
                    </Box>
                );
            },
        },
        {
            field: 'selling_price',
            headerName: 'Selling Price', headerAlign: 'right', align: 'right',
            sortable: false, width: 120,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
                    {row.selling_price ? formatCurrency(row.selling_price) : '—'}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ fontFamily: T.FONT }}>
            <PageTitle>Channel Order Details</PageTitle>

            {/* ── Status banner ─────────────────────────────────────────── */}
            <Box sx={{ borderRadius: '14px', border: `1px solid ${T.BORDER}`, bgcolor: T.WHITE, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ height: 4, bgcolor: T.PRIMARY }} />
                <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography sx={{ fontFamily: T.FONT, fontWeight: 700, fontSize: 20, color: T.NAVY, letterSpacing: '0.3px' }}>
                                {order?.order_number || order?.orderName || '—'}
                            </Typography>
                            {order?.order_number && <CopyIcon text={order.order_number} />}
                        </Stack>
                        <Typography sx={{ fontFamily: T.FONT, fontSize: 12.5, color: T.SUBTLE, mt: 0.25 }}>
                            {[order?.merchantName, order?.channel, order?.createdDate ? formatDate(order.createdDate, true) : null].filter(Boolean).join('  ·  ') || '—'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PaymentBadge mode={payment_mode} />
                        <StatusChip status={order?.channelOrderStatus} />
                    </Stack>
                </Box>
            </Box>

            {/* ── Summary tiles ─────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} md={3}><Tile label="Invoice Value" value={formatCurrency(order?.invoice_value ?? 0)} accent={T.SUCCESS} /></Grid>
                <Grid item xs={6} md={3}><Tile label="Total Cost" value={formatCurrency(order?.subtotal_price_after_discount ?? 0)} accent={T.PRIMARY} /></Grid>
                <Grid item xs={6} md={3}><Tile label="Shipping Cost" value={formatCurrency(order?.total_shipping_price ?? 0)} accent={T.WARNING} /></Grid>
                <Grid item xs={6} md={3}><Tile label="Items" value={order?.orderItems?.length ?? 0} accent={T.INFO} /></Grid>
            </Grid>

            {/* ── Order + Customer details ──────────────────────────────── */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <SectionCard title="Order Details">
                        <InfoRow label="Order Number" value={order?.order_number} />
                        <InfoRow label="Order Name" value={order?.orderName} />
                        <InfoRow label="Channel Order ID" value={order?.channelOrderId} />
                        <InfoRow label="Channel" value={order?.channel} />
                        <InfoRow label="Merchant" value={order?.merchantName} />
                        <InfoRow label="Order Date" value={order?.createdDate ? formatDate(order.createdDate, true) : null} />
                        <InfoRow label="Status" value={<StatusChip status={order?.channelOrderStatus} />} />
                        <InfoRow label="Payment Mode" value={<PaymentBadge mode={payment_mode} />} />
                    </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionCard title="Customer & Shipping">
                        <InfoRow label="Customer Name" value={customerName || null} />
                        <InfoRow label="Phone" value={masked ? maskPhoneNumber(order?.shipping_address?.phone) : (order?.shipping_address?.phone || null)} />
                        <InfoRow label="Email" value={masked ? maskEmail(order?.shipping_address?.email) : (order?.shipping_address?.email || null)} />
                        <InfoRow
                            label="Shipping Address"
                            value={masked
                                ? maskAddress(
                                    order?.shipping_address?.address1, order?.shipping_address?.address2,
                                    order?.shipping_address?.city, order?.shipping_address?.state,
                                    order?.shipping_address?.country, order?.shipping_address?.zip,
                                )
                                : (fullAddress || null)}
                        />
                    </SectionCard>
                </Grid>

                {/* ── NETRA scores ──────────────────────────────────────── */}
                {/* Frozen at channel-order ingest: never re-derived from the ProShip
                    order(s) this becomes, so it can differ from the same shipment's
                    score on the Orders page. Each card says so explicitly. Both
                    render nothing when unscored. */}
                {order?.netra_address && (
                    <Grid item xs={12} md={6}>
                        <AddressQualityCard
                            netra={order.netra_address}
                            orderId={order.id}
                            scorePath={`channel-order/${order.id}/netra-score`}
                            scoredStageNote={netraStageNote(order.netra_address)}
                        />
                    </Grid>
                )}
                {order?.netra_rto && (
                    <Grid item xs={12} md={6}>
                        <RtoRiskCard
                            netra={order.netra_rto}
                            orderId={order.id}
                            scorePath={`channel-order/${order.id}/netra-score`}
                            scoredStageNote={netraStageNote(order.netra_rto)}
                        />
                    </Grid>
                )}

                {/* ── Shipment Items ────────────────────────────────────── */}
                <Grid item xs={12}>
                    <SectionCard title="Shipment Items" sx={{ mb: 0 }}>
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight={true}
                            rows={courier || []}
                            filterMode={"server"}
                            getRowHeight={() => 'auto'}
                            rowCount={order?.orderItems?.length ?? 0}
                            getRowId={(row) => Math.random()}
                            sx={gridSx}
                        />
                    </SectionCard>
                </Grid>
            </Grid>

        </Box>
    );
}

export default ViewDetails;
